import { useAdminReviewForms, type AdminReviewForm, type AdminReviewFormSort } from '@fidalia/api';
import {
  Alert,
  Avatar,
  Button,
  buttonVariants,
  DataTable,
  Input,
  PageHeader,
  Pagination,
  Select,
  type TableColumn,
} from '@fidalia/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  MdAssignment,
  MdCheck,
  MdContentCopy,
  MdOpenInNew,
  MdSearch,
  MdSort,
} from 'react-icons/md';

import { Etoiles } from '@/features/reviews/etoiles';
import { errorMessage } from '@/lib/auth';
import { useDebounced } from '@/lib/use-debounced';

const PAGE_SIZE = 20;

export const Route = createFileRoute('/_app/forms')({
  component: FormulairesPage,
});

/** Copie `texte` dans le presse-papiers ; `copie` reste vrai 2 s pour confirmer. */
function useCopie(texte: string) {
  const [copie, setCopie] = useState(false);

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(texte);
    } catch {
      return; // presse-papiers bloqué : rien à confirmer
    }
    setCopie(true);
    setTimeout(() => setCopie(false), 2000);
  };

  return { copie, copier };
}

/** Lien tronqué et cliquable, avec une icône pour le copier. */
function LienGoogle({ url }: { url: string }) {
  const { copie, copier } = useCopie(url);

  return (
    <div className="flex max-w-56 items-center gap-1.5">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        title={url}
        className="min-w-0 truncate text-brand-600 hover:underline"
      >
        {url}
      </a>
      <button
        type="button"
        onClick={() => void copier()}
        aria-label="Copier le lien"
        title={copie ? 'Copié' : 'Copier le lien'}
        className="shrink-0 rounded p-1 text-content-muted hover:bg-surface-muted hover:text-content"
      >
        {copie ? <MdCheck className="size-4 text-success" /> : <MdContentCopy className="size-4" />}
      </button>
    </div>
  );
}

/** Consulter le formulaire tel que le patient le voit, ou en copier le lien. */
function ActionsFormulaire({ url }: { url: string }) {
  const { copie, copier } = useCopie(url);

  return (
    <div className="flex items-center justify-end gap-2">
      <Button
        variant="ghost"
        size="sm"
        icon={copie ? <MdCheck className="size-4" /> : <MdContentCopy className="size-4" />}
        onClick={() => void copier()}
      >
        {copie ? 'Lien copié' : 'Copier le lien'}
      </Button>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className={buttonVariants({ variant: 'outline', size: 'sm' })}
      >
        <MdOpenInNew className="size-4" aria-hidden="true" />
        Consulter
      </a>
    </div>
  );
}

const TRIS: readonly { valeur: AdminReviewFormSort; label: string }[] = [
  { valeur: 'name', label: 'Nom (A → Z)' },
  { valeur: 'reviews', label: 'Plus d’avis' },
  { valeur: 'best', label: 'Mieux notés' },
  { valeur: 'worst', label: 'Moins bien notés' },
];

const columns: TableColumn<AdminReviewForm>[] = [
  {
    key: 'cabinet',
    title: 'Cabinet',
    cell: (f) => (
      <div className="flex items-center gap-3">
        <Avatar name={f.cabinetNom} size="sm" tone="auto" />
        <div className="flex min-w-0 flex-col items-start">
          {f.cabinetUserId ? (
            <Link
              to="/users/$id"
              params={{ id: f.cabinetUserId }}
              className="font-medium text-brand-600 hover:underline"
            >
              {f.cabinetNom}
            </Link>
          ) : (
            <span className="font-medium">{f.cabinetNom}</span>
          )}
          {f.cabinetEmail ? (
            <span className="truncate text-xs text-content-muted">{f.cabinetEmail}</span>
          ) : null}
        </div>
      </div>
    ),
  },
  {
    key: 'google',
    title: 'Fiche Google',
    cell: (f) =>
      f.googleReviewUrl ? (
        <LienGoogle url={f.googleReviewUrl} />
      ) : (
        <span className="text-content-subtle">—</span>
      ),
  },
  {
    key: 'reviews',
    title: 'Note moyenne',
    cell: (f) =>
      f.average === null ? (
        <span className="text-content-subtle">—</span>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-content-muted tabular-nums">
            {f.average.toFixed(1).replace('.', ',')}
          </span>
          <Etoiles valeur={Math.round(f.average)} />
          <Link
            to="/reviews"
            search={{ cabinetId: f.cabinetId }}
            className="text-sm font-medium text-brand-600 hover:underline"
          >
            {f.reviews} avis
          </Link>
        </div>
      ),
  },
  {
    key: 'views',
    title: 'Ouvertures',
    cell: (f) => <span className="tabular-nums">{f.formViews}</span>,
  },
  {
    key: 'url',
    align: 'right',
    cell: (f) => <ActionsFormulaire url={f.url} />,
  },
];

function FormulairesPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);
  const [sort, setSort] = useState<AdminReviewFormSort>('name');
  const { data, error } = useAdminReviewForms({
    page,
    limit: PAGE_SIZE,
    search: debouncedSearch,
    sort,
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={<MdAssignment />}
        title="Formulaires"
        description="Le formulaire d’avis de chaque cabinet, tel que le patient le voit."
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          type="search"
          aria-label="Rechercher un cabinet"
          placeholder="Nom du cabinet…"
          icon={<MdSearch className="size-6" />}
          containerClassName="h-11 w-full sm:min-w-64 sm:flex-1"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />

        <Select
          aria-label="Trier"
          icon={<MdSort className="size-5" />}
          containerClassName="h-11 w-full sm:w-56"
          value={sort}
          onChange={(event) => {
            setSort(event.target.value as AdminReviewFormSort);
            setPage(1);
          }}
        >
          {TRIS.map((tri) => (
            <option key={tri.valeur} value={tri.valeur}>
              {tri.label}
            </option>
          ))}
        </Select>
      </div>

      {error ? <Alert tone="danger">{errorMessage(error, 'Chargement impossible.')}</Alert> : null}

      <DataTable
        mobileCards
        data={data?.items ?? []}
        columns={columns}
        isLoading={!data}
        skeletonRows={10}
        rowId={(f) => f.cabinetId}
        emptyMessage={
          debouncedSearch ? `Aucun résultat pour « ${debouncedSearch} ».` : 'Aucun cabinet.'
        }
      />

      {data ? (
        <Pagination
          page={page}
          total={data.total}
          limit={PAGE_SIZE}
          onChange={setPage}
          label="cabinets"
        />
      ) : null}
    </div>
  );
}
